import {AfterViewInit, DestroyRef, Directive, ElementRef} from '@angular/core';
import {CdkTextareaAutosize} from "@angular/cdk/text-field";

/**
 * Får et textarea med cdkTextareaAutosize til å beregne høyden på nytt når bredden endres.
 *
 * cdkTextareaAutosize beregner kun høyden ved endring av tekst og ved resize av vinduet. Dersom textarea blir tegnet mens
 * omkringliggende layout ennå ikke har fått endelig bredde (f.eks. ved veksling mellom saksliste og splittvisning med saksdetaljer),
 * beregnes høyden ut fra feil bredde (placeholder brekkes over mange linjer) og feltet forblir unaturlig høyt.
 * Det samme gjelder alle andre breddeendringer som ikke skyldes vinduet, f.eks. at scrollbar dukker opp eller en drawer åpnes.
 *
 * ResizeObserver kalles etter layout og før tegning, så ny høyde er på plass uten at feltet blinker.
 * Se https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver
 *
 * Når nettleserstøtten er god nok (se .browserslistrc og iOS-versjoner for appen), kan både dette direktivet og
 * cdkTextareaAutosize erstattes med CSS-egenskapen `field-sizing: content` (med min-height/max-height i stedet for
 * cdkAutosizeMinRows/cdkAutosizeMaxRows). Da beregner nettleseren høyden som en del av layout, og den stemmer alltid med bredden.
 * Storyene til direktivet tester oppførsel og kan brukes til å verifisere en slik overgang.
 * Se https://developer.mozilla.org/en-US/docs/Web/CSS/field-sizing
 *
 * Bruk:
 * ```
 * <textarea cdkTextareaAutosize tilpassAutosizeVedBreddeendring></textarea>
 * ```
 */
@Directive({
    selector: 'textarea[cdkTextareaAutosize][tilpassAutosizeVedBreddeendring]',
    standalone: true
})
export class TilpassAutosizeVedBreddeendringDirektiv implements AfterViewInit {

    constructor(private el: ElementRef<HTMLTextAreaElement>,
                private autosize: CdkTextareaAutosize,
                private destroyRef: DestroyRef) {
    }

    ngAfterViewInit(): void {
        if (typeof ResizeObserver === 'undefined') return; //f.eks. i jsdom (jest)

        const tekstfelt = this.el.nativeElement;
        //bredden cdkTextareaAutosize beregnet høyden ut fra. Må leses nå: endres bredden før første observasjon, ville endringen ellers ikke bli oppdaget
        let beregnetBredde = tekstfelt.offsetWidth;
        const observatør = new ResizeObserver(() => {
            const bredde = tekstfelt.offsetWidth;
            //høydeendringer (bl.a. de vi selv utløser) krever ikke ny beregning, og skjult felt kan ikke måles
            if (bredde === beregnetBredde || bredde === 0) return;
            beregnetBredde = bredde;
            this.beregnHøyde(tekstfelt);
        });
        observatør.observe(tekstfelt);

        this.destroyRef.onDestroy(() => observatør.disconnect());
    }

    private beregnHøyde(tekstfelt: HTMLTextAreaElement) {
        //resizeToFitContent(true) alene er ikke nok: cdkTextareaAutosize mellomlagrer høyden til placeholder, målt ved gammel bredde.
        //Å sette placeholder på nytt tømmer mellomlageret og måler på nytt. Avhenger av udokumentert oppførsel i CDK (dekkes av storyene)
        const {selectionStart, selectionEnd, selectionDirection} = tekstfelt;
        this.autosize.placeholder = this.autosize.placeholder;
        //målingen av placeholder bytter verdien i feltet midlertidig, som flytter markøren til slutten av teksten
        if (document.activeElement === tekstfelt) tekstfelt.setSelectionRange(selectionStart, selectionEnd, selectionDirection ?? undefined);

        this.autosize.resizeToFitContent(true);
    }
}
